import { HttpClient } from '@angular/common/http';
import { ApiResponse } from '@core/models/api-response.model';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Member } from '@core/models/member.model';
import { map, shareReplay } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { TrafficSource, TrafficSourceNames } from '@core/enums/traffic-source.enum';
import { AesService } from '@core/services/aes.service';
import { SocketIoService } from './socket.io-service';

@Injectable({
  providedIn: 'root'
})
export class MemberHttpService {

  private onlineMembers: BehaviorSubject<{ id: number, name: string }[]> = new BehaviorSubject<{ id: number, name: string }[]>(null);
  private onlineMembers$: Observable<{ id: number, name: string }[]> = this.onlineMembers.asObservable();
  private onlineMemberIds$ = this.onlineMembers$.pipe(
    map(members => new Set(members.map(member => member.id) ?? [])),
    shareReplay(1)
  );

  constructor(
    private http: HttpClient,
    private aesService: AesService,
    private socketIoService: SocketIoService
  ) {
    this.socketIoService.joinPresenceChannel('member-online').then(presence => {
      presence
        .here((members: { id: number, name: string }[]) => {
          if (this.onlineMembers.getValue() == null) {
            this.onlineMembers.next(members);
          }
        })
        .joining((member: { id: number, name: string }) => {
          this.memberJoining(member);
        })
        .leaving((member: { id: number, name: string }) => {
          this.memberLeaving(member);
        })
    })
  }

  isMemberOnline(id: number): Observable<boolean> {
    return this.onlineMemberIds$.pipe(
      map(onlineIds => onlineIds.has(id))
    );
  }

  private memberJoining(member: { id: number, name: string }): void {
    let members: { id: number, name: string }[] = this.onlineMembers.getValue() ?? [];
    members.push(member);
    this.onlineMembers.next(members);
  }

  private memberLeaving(member: { id: number, name: string }): void {
    let members: { id: number, name: string }[] = this.onlineMembers.getValue() ?? [];
    members = members.filter(x => x.id != member.id && x.name != member.name);
    this.onlineMembers.next(members);
  }

  resetPassword(member: Member, form: FormGroup) {
    // encrypt the raw password before sending to api
    if (form.value.password) {
      form.value.password = this.aesService.encryptWithReqSignKey(form.value.password);
    }
    return this.http.post<ApiResponse>(`/member/${member.id}/password`, form.value);
  }

  getAllDropdown(): Observable<Member[]> {
    return this.http.get<ApiResponse>(`/member`).pipe(
      map(res => {
        Object.keys(res.data.rows).forEach(key => {
          res.data.rows[key].custom = res.data.rows[key].id + ' - ' + res.data.rows[key].username.toUpperCase() + ' - ' + res.data.rows[key].name.toUpperCase();
        })
        return res.data.rows
      }
      ));
  }

  getSourceTypesDropdown(): Observable<any[]> {
    return this.http.get<ApiResponse>(`/sourcetypes`).pipe(
      map(response => {
        // change traffic source name to frontend name
        const transformedTypes = response.data.rows.map(type => ({
          id: type.id,
          name: TrafficSourceNames[type.id]
        }));

        const organicType = transformedTypes.find(t => t.id === TrafficSource.ORGANIC);
        const otherTypes = transformedTypes.filter(t => t.id !== TrafficSource.ORGANIC);

        return organicType ? [organicType, ...otherTypes] : transformedTypes;
      })
    );
  }

}
